import { NativeBridge } from "@swiggy-private/native-bridge";
import {
    GetConfigValueRequestPayload,
    GetSwiggyHeadersResponsePayload,
} from "@swiggy-private/native-bridge/dist/interfaces";
import { mockSwgyHeaders } from "../tests/mocks";

let bridgeSdk: NativeBridge | null = null;

export const initBridgeSdk = (sdk?: NativeBridge): NativeBridge => {
    if (bridgeSdk && !sdk) {
        return bridgeSdk;
    }

    bridgeSdk = sdk || new NativeBridge(null);
    return bridgeSdk;
};

/**
 * get instance for bridge sdk via getBridgeSdk
 * @returns instance for bridge SDK
 */
export const getBridgeSdk = (): NativeBridge => bridgeSdk || initBridgeSdk();

export const isBrowserEnv = () => typeof window !== "undefined";

let swHeaders: GetSwiggyHeadersResponsePayload | undefined;

export const getSwiggyHeaders = async (): Promise<GetSwiggyHeadersResponsePayload | undefined> => {
    // swHeaders is already initialized
    if (swHeaders) {
        return swHeaders;
    }

    // get header value, store & return
    if (process.env.NODE_ENV === "development") {
        // In development, headers come from a static mock (tests/mocks.ts). Tests can
        // override individual fields (e.g. { platform: "swiggy-mweb" } to exercise the
        // non-native V1 flow) by setting window.__SWIGGY_MOCK_HEADERS__ before the app
        // boots (via addInitScript); the override is merged over the default mock.
        // No effect in production.
        const injectedHeaders =
            typeof window !== "undefined"
                ? ((window as unknown as Record<string, unknown>).__SWIGGY_MOCK_HEADERS__ as
                      | Partial<GetSwiggyHeadersResponsePayload>
                      | undefined)
                : undefined;
        const mockHeaders = { ...mockSwgyHeaders, ...(injectedHeaders || {}) };

        swHeaders = mockHeaders;
        return mockHeaders;
    } else {
        swHeaders = await getBridgeSdk()
            .getSwiggyHeadersFromClient()
            .catch(() => {
                return Promise.resolve(undefined);
            });
    }

    return swHeaders;
};

export const closeWebview = () => {
    const sdk = getBridgeSdk();
    if (sdk?.close) {
        return sdk.close().catch(() => void 0);
    } else {
        window.location.href = "/";
    }
};

export const enableBackSwipe = (isEnabled: boolean): void => {
    try {
        getBridgeSdk()
            .enableBackSwipe({ isEnabled })
            .catch(() => void 0);
    } catch (_) {
        void 0;
    }
};

export const changeWebviewHeight = (height: number) => {
    const sdk = getBridgeSdk();
    if (sdk?.updateWebViewHeight) {
        return sdk
            .updateWebViewHeight({
                height,
            })
            .catch(() => void 0);
    }
};

export const getDynamicConfigValues = (requestPayload: GetConfigValueRequestPayload) => {
    const sdk = getBridgeSdk();

    return sdk?.getConfigValue(requestPayload);
};
